import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { SmartTagsWysiwygLayoutProps } from 'ui-builder';
import QuillWysiwygLayout from '../../../form/input/layout/QuillWysiwygLayout';
import HowToUseSmartTagsLink from '../../../form/HowToUseSmartTagsLink';
import { Tag } from '../../../form/input/SmartTagsWysiwyg';

import './SmartTagsWysiwygLayout.scss';
import useFeatureToggleService from 'feature-toggle';

function SmartTagsQuillLayoutWrapper(props: SmartTagsWysiwygLayoutProps) {

  const featureToggleService = useFeatureToggleService();

  const notDeprecatedTags = useMemo(
    () => props.availableTags.filter((tag) => !tag.isDeprecated),
    [props.availableTags.length],
  );

  useEffect(() => {
    (window as any).smartTags = notDeprecatedTags;
  }, [notDeprecatedTags.length]);

  const quillRef = useRef<{
    pasteTextOnCursorPosition: (text: string) => void;
  } | null>(null);

  const modules = {
    mention: {
      allowedChars: /^[A-Za-z\\.]*$/,
      dataAttributes: ['id', 'value', 'existing'],
      mentionDenotationChars: ['#'],
      source: useCallback((searchTerm: string, renderList: any, mentionChar: string) => {
        if (mentionChar === '#') {
          const allTags = (window as any).smartTags || [];

          const currentSegment = searchTerm.includes('.')
            ? searchTerm.split('.')
              .slice(0, -1)
              .join('.')
            : '';

          const currentSegmentPrefix = currentSegment !== '' ? `${currentSegment}.` : '';

          const currentSegmentTags = currentSegment !== '' ? allTags
            .filter((tag: Tag) => tag.value.startsWith(currentSegmentPrefix))
            : allTags;

          const isChooseSegmentMode = () => {
            if (featureToggleService.isNewSmartTagsEnabled()) {
              if (currentSegment.length === 0) {
                return true;
              } else {
                return !currentSegmentTags
                  .find(
                    (tag: Tag) => !tag.value.replace(currentSegmentPrefix, '').includes('.')
                  );
              }
            } else {
              return false;
            }
          };

          let values: Tag[];

          if (isChooseSegmentMode()) {
            values = (Array
              .from(
                new Set(
                  currentSegmentTags
                    .map((tag: Tag) => ({
                      ...tag,
                      id: `segment:${tag.value}`,
                      value: currentSegment !== '' ? tag.value.replace(currentSegmentPrefix, '') : tag.value,
                    }))
                    .map((tag: Tag) => tag.value.split('.')[0])
                )
              ) as string[])
              .map((tagName: string) => ({
                id: `segment:${tagName}`,
                value: `${currentSegmentPrefix}${tagName}`,
              })) as Tag[];
          } else {
            values = currentSegmentTags;
          }

          if (searchTerm.length === 0) {
            renderList(values, searchTerm);
          } else {
            const matches = [];
            for (let i = 0; i < values.length; i += 1) {
              if (values[i].value.toLowerCase()
                .indexOf(searchTerm.toLowerCase()) !== -1) {
                matches.push(values[i]);
              }
            }
            renderList(matches, searchTerm);
          }
        }
      }, []),
      onSelect: useCallback((item: any, insertItem: any) => {
        if (item.id.startsWith('segment:')) {
          quillRef?.current?.pasteTextOnCursorPosition(`${item.value}.`);
        } else {
          insertItem(item);
        }
      }, []),
      renderItem: useCallback((item: any, searchTerm: string) => {
        const div = document.createElement('div');
        div.textContent = item.value;
        return div;
      }, []),
      onBeforeClose: useCallback((quill: any) => {
        return false;
      }, []),
    },
  };

  return (
    <>
      {!props.hideHowToUseLink && (
        <HowToUseSmartTagsLink />
      )}
      <QuillWysiwygLayout
        {...props}
        modules={modules}
        quillRef={quillRef}
      />
    </>
  );
}

export default function SmartTagsWysiwygLayout(
  props: SmartTagsWysiwygLayoutProps,
) {

  return (
    <SmartTagsQuillLayoutWrapper
      {...props}
    />
  );
}
